/**
 * Sprint 14.1b — "+ New Evaluation" modal.
 *
 * Inputs:
 *   - Name (required, free text)
 *   - Pipeline (required, single-select)
 *   - Role (required, single-select)
 *   - Department (required, single-select)
 *   - Sub-Department (optional, single-select, cascades from Department)
 *
 * On submit, POSTs to /api/v1/candidates/{candidateId}/evaluations and calls
 * onCreated with the new evaluation row. The parent is responsible for
 * switching the pill selection to the new ID.
 *
 * Pre-fill props (initial*) come from the candidate's current values so the
 * recruiter only has to type a name in the simple case.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { v1Get, v1Post } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { formatMasterOptionLabel } from '@/lib/masterDataLabels';

export function NewEvaluationModal({
  open,
  onOpenChange,
  candidateId,
  initialName = '',
  initialRoleId = '',
  initialDepartmentId = '',
  initialSubDepartmentId = '',
  initialPipelineId = '',
  onCreated,
}) {
  const [name, setName] = React.useState(initialName);
  const [roleId, setRoleId] = React.useState(initialRoleId);
  const [departmentId, setDepartmentId] = React.useState(initialDepartmentId);
  const [subDepartmentId, setSubDepartmentId] = React.useState(initialSubDepartmentId);
  const [pipelineId, setPipelineId] = React.useState(initialPipelineId);
  const [submitting, setSubmitting] = React.useState(false);

  // Re-prime defaults whenever the modal is opened (candidate may change).
  React.useEffect(() => {
    if (open) {
      setName(initialName);
      setRoleId(initialRoleId);
      setDepartmentId(initialDepartmentId);
      setSubDepartmentId(initialSubDepartmentId);
      setPipelineId(initialPipelineId);
    }
  }, [open, initialName, initialRoleId, initialDepartmentId, initialSubDepartmentId, initialPipelineId]);

  const { data: pipelines = [] } = useQuery({
    queryKey: ['v1-pipelines'],
    queryFn: () => v1Get('/pipelines'),
    enabled: open,
  });
  const { data: roles = [] } = useQuery({
    queryKey: ['v1-master-roles'],
    queryFn: () => v1Get('/master/roles'),
    enabled: open,
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['v1-master-departments'],
    queryFn: () => v1Get('/master/departments'),
    enabled: open,
  });

  const subDepartments =
    departments.find((d) => d.id === departmentId)?.sub_departments || [];

  const canSubmit =
    name.trim().length > 0 && pipelineId && roleId && departmentId && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const created = await v1Post(`/candidates/${candidateId}/evaluations`, {
        name: name.trim(),
        pipeline_id: pipelineId,
        role_id: roleId,
        department_id: departmentId,
        sub_department_id: subDepartmentId || null,
      });
      toast.success(`Evaluation “${created.name}” started`);
      onOpenChange(false);
      onCreated?.(created);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not create evaluation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-evaluation-dialog">
        <DialogHeader>
          <DialogTitle>New Evaluation</DialogTitle>
          <DialogDescription>
            Start a fresh evaluation round for this candidate. The pipeline locks once any
            stage activity occurs — you can still rename it after that.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium text-muted-foreground">
              Evaluation Name <span className="text-rose-600">*</span>
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Senior Frontend — Round 2"
              data-testid="new-evaluation-name-input"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">
              Pipeline <span className="text-rose-600">*</span>
            </Label>
            <Select value={pipelineId} onValueChange={setPipelineId}>
              <SelectTrigger data-testid="new-evaluation-pipeline-select">
                <SelectValue placeholder="Select pipeline" />
              </SelectTrigger>
              <SelectContent>
                {pipelines.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label || p.name || p.pipeline_key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Pipeline becomes immutable once the first stage activity occurs.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label className="text-xs font-medium text-muted-foreground">
                Role <span className="text-rose-600">*</span>
              </Label>
              <Select value={roleId} onValueChange={setRoleId}>
                <SelectTrigger data-testid="new-evaluation-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {roles
                    .filter((r) => r.status === 'active')
                    .map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {formatMasterOptionLabel(r)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground">
                Department <span className="text-rose-600">*</span>
              </Label>
              <Select
                value={departmentId}
                onValueChange={(v) => {
                  setDepartmentId(v);
                  setSubDepartmentId('');
                }}
              >
                <SelectTrigger data-testid="new-evaluation-department-select">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments
                    .filter((d) => d.status === 'active')
                    .map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {formatMasterOptionLabel(d)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {subDepartments.length > 0 && (
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Sub-Department</Label>
              <Select value={subDepartmentId} onValueChange={setSubDepartmentId}>
                <SelectTrigger data-testid="new-evaluation-sub-department-select">
                  <SelectValue placeholder="Select sub-department" />
                </SelectTrigger>
                <SelectContent>
                  {subDepartments.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="new-evaluation-submit-button"
          >
            {submitting ? 'Creating…' : 'Create Evaluation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
